import { camelizeKeys, decamelizeKeys } from "humps";
import {
  Order,
  OrderModificationPayload,
  OrdersPayload,
  OrdersResponse,
} from "../types/order.types";
import { ShoppingCartPayload } from "../types/shopping-cart.types";
import { getApiUrl } from "@dashboard/common/utils";

export const fetchGetOrders = async ({
  token,
  page,
  perPage,
  orderBy,
  stageGroup,
  categories,
  from,
  to,
}: OrdersPayload): Promise<OrdersResponse> => {
  const response = await fetch(
    `${getApiUrl()}/api/ms/order/all?${new URLSearchParams({
      page: String(page),
      "per-page": String(perPage),
      "order-by": orderBy,
      "state-group": stageGroup,
      ...(categories ? { categories } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    })}`,
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "x-api-version": "3",
      } as { [key: string]: string },
    }
  );
  if (response.status === 200) {
    const responseJson = await response.json();
    return camelizeKeys(responseJson) as OrdersResponse;
  } else {
    throw new Error();
  }
};

export const fetchGetOrder = async ({
  id,
  token,
}: {
  id: number;
  token: string;
}): Promise<Order | undefined> => {
  const response = await fetch(`${getApiUrl()}/api/ms/order/${id}/details`, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "x-api-version": "2",
    },
  });
  if (response.status !== 200) throw new Error();
  else {
    const responseJson = await response.json();
    return camelizeKeys(responseJson) as Order;
  }
};

export const fetchUpdateOrder = async ({
  id,
  token,
  data,
}: {
  id: number;
  token: string;
  data: OrderModificationPayload;
}): Promise<Order | undefined> => {
  const response = await fetch(
    `${getApiUrl()}/api/ms/order/modification/${id}/modification`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-api-version": "1",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(decamelizeKeys(data)),
    }
  );
  if (response.status !== 200) throw new Error();
  else {
    const responseJson = await response.json();
    return camelizeKeys(responseJson) as Order;
  }
};

export const fetchCalculateShoppingCart = async ({
  token,
  data,
}: {
  token: string;
  data: ShoppingCartPayload;
}): Promise<ShoppingCartPayload> => {
  const response = await fetch(
    `${getApiUrl()}/api/ms/calculator/shopping-cart`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(decamelizeKeys(data)),
    }
  );
  if (response.status !== 200) throw new Error();
  const responseJson = await response.json();
  return camelizeKeys(responseJson) as ShoppingCartPayload;
};

export const fetchCreateOrder = async ({
  token,
  data,
}: {
  token: string;
  data: ShoppingCartPayload;
}): Promise<Order> => {
  const response = await fetch(`${getApiUrl()}/api/ms/order`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
      "x-api-version": "2",
    },
    body: JSON.stringify(decamelizeKeys(data)),
  });
  if (response.status !== 200) throw new Error();
  const responseJson = await response.json();
  return camelizeKeys(responseJson) as Order;
};
